import { api } from './client'
import type { ExploreLeague, League, LeagueJoinPolicy, LeagueJoinRequest } from '../types'

export function loadLeagues(): Promise<League[]> {
  return api.get('/leagues')
}

export function createLeague(
  name: string,
  motto?: string,
  joinPolicy?: LeagueJoinPolicy
): Promise<League> {
  return api.post('/leagues', { name, motto, joinPolicy })
}

/** The Explore directory. Only mini leagues set to `open` or `request` come back. */
export function exploreLeagues(query: string, offset = 0): Promise<ExploreLeague[]> {
  const params = new URLSearchParams({ offset: String(offset) })
  if (query) params.set('q', query)
  return api.get(`/leagues/explore?${params}`)
}

/**
 * Join (policy `open`) or ask to join (policy `request`) from Explore. The two answer differently:
 * an open league returns the joined league, a request league returns `{ status: 'requested' }`.
 */
export function joinLeagueById(leagueId: string): Promise<League | { status: 'requested' }> {
  return api.post(`/leagues/${leagueId}/join`)
}

/** Owner-only. Switching to `invite` also declines whatever was pending. */
export function updateJoinPolicy(leagueId: string, joinPolicy: LeagueJoinPolicy): Promise<League> {
  return api.put(`/leagues/${leagueId}/join-policy`, { joinPolicy })
}

/** Owner-only: who is waiting to be let in. */
export function loadJoinRequests(leagueId: string): Promise<LeagueJoinRequest[]> {
  return api.get(`/leagues/${leagueId}/join-requests`)
}

export function acceptJoinRequest(leagueId: string, requestId: string): Promise<League> {
  return api.post(`/leagues/${leagueId}/join-requests/${requestId}/accept`)
}

export function declineJoinRequest(leagueId: string, requestId: string): Promise<{ ok: true }> {
  return api.post(`/leagues/${leagueId}/join-requests/${requestId}/decline`)
}

/** Admin-only. An empty string clears the motto, returning it to the unset state. */
export function updateLeagueMotto(leagueId: string, motto: string): Promise<League> {
  return api.put(`/leagues/${leagueId}/motto`, { motto })
}

export function joinLeague(inviteCode: string): Promise<League> {
  return api.post('/leagues/join', { inviteCode })
}

export function leaveLeague(leagueId: string): Promise<{ ok: true }> {
  return api.post(`/leagues/${leagueId}/leave`)
}

export function inviteToLeague(leagueId: string, userId: string): Promise<League> {
  return api.post(`/leagues/${leagueId}/invite`, { userId })
}

